import { type TouchEvent, useState, useCallback, useMemo } from 'react';

export enum Direction {
  horizontal = 'horizontal',
  vertical = 'vertical',
  all = 'all'
}

type UseOnSwipeActions = {
  onSwipeLeft?: (event: TouchEvent) => void;
  onSwipeRight?: (event: TouchEvent) => void;
  onSwipeUp?: (event: TouchEvent) => void;
  onSwipeDown?: (event: TouchEvent) => void;
};

type UseOnSwipeOptions = {
  minSwipeDistance?: number;
};

export const useOnSwipe = (
  direction: Direction,
  {
    onSwipeLeft = () => {},
    onSwipeRight = () => {},
    onSwipeUp = () => {},
    onSwipeDown = () => {}
  }: UseOnSwipeActions,
  options?: UseOnSwipeOptions
) => {
  const { minSwipeDistance = 50 } = options || {};

  const [touchStartX, setTouchStartX] = useState<number>(-1);
  const [touchEndX, setTouchEndX] = useState<number>(-1);
  const [touchStartY, setTouchStartY] = useState<number>(-1);
  const [touchEndY, setTouchEndY] = useState<number>(-1);

  const isHorizontalSwipe = useMemo(() => direction === Direction.horizontal, [direction]);
  const isVerticalSwipe = useMemo(() => direction === Direction.vertical, [direction]);
  const isAllWaySwipe = useMemo(() => direction === Direction.all, [direction]);

  const onTouchStart = useCallback((event: TouchEvent) => {
    setTouchEndX(-1); // otherwise the swipe is fired even with usual touch events
    setTouchEndY(-1); // otherwise the swipe is fired even with usual touch events
    setTouchStartX(event.targetTouches[0].clientX);
    setTouchStartY(event.targetTouches[0].clientY);
  }, []);

  const onTouchMove = useCallback((event: TouchEvent) => {
    setTouchEndX(event.targetTouches[0].clientX);
    setTouchEndY(event.targetTouches[0].clientY);
  }, []);

  const onTouchEnd = useCallback(
    (event: TouchEvent) => {
      if (isHorizontalSwipe && (touchStartX < 0 || touchEndX < 0)) return;
      if (isVerticalSwipe && (touchStartY < 0 || touchEndY < 0)) return;

      const distanceX = touchStartX - touchEndX;
      const distanceY = touchStartY - touchEndY;

      const isLeftSwipe =
        (isHorizontalSwipe || isAllWaySwipe) &&
        distanceX > minSwipeDistance &&
        Math.abs(distanceX) > Math.abs(distanceY);
      const isRightSwipe =
        (isHorizontalSwipe || isAllWaySwipe) &&
        distanceX < -minSwipeDistance &&
        Math.abs(distanceX) > Math.abs(distanceY);
      const isUpSwipe =
        (isVerticalSwipe || isAllWaySwipe) &&
        distanceY > minSwipeDistance && // check if this condition is the right one
        Math.abs(distanceY) > Math.abs(distanceX);
      const isDownSwipe =
        (isVerticalSwipe || isAllWaySwipe) &&
        distanceY < minSwipeDistance && // check if this condition is the right one
        Math.abs(distanceY) > Math.abs(distanceX);

      if (isLeftSwipe && onSwipeLeft) onSwipeLeft(event);
      if (isRightSwipe && onSwipeRight) onSwipeRight(event);
      if (isUpSwipe && onSwipeUp) onSwipeUp(event);
      if (isDownSwipe && onSwipeDown) onSwipeDown(event);
    },
    [
      isAllWaySwipe,
      isHorizontalSwipe,
      isVerticalSwipe,
      minSwipeDistance,
      onSwipeDown,
      onSwipeLeft,
      onSwipeRight,
      onSwipeUp,
      touchEndX,
      touchStartX,
      touchEndY,
      touchStartY
    ]
  );

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd
  };
};
